import React from 'react';
import { ValidatingInput, ValidatingSelect } from '@/components/forms/validation';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { toNullableNumber, classnames } from '@/utils/html';
import { Button } from '@/components/Buttons';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import theme from './PriceRowEditor.module.scss';
import { ProjectFeeSet, TranslatableText, ItemCategory } from '@/types';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { PriceRow } from '@/seller/components/PriceRow';
import { FeePreviewCompact } from '@/seller/components/fees';
import { useIsMobile } from '@/utils/hooks';
import ItemCategorySelect from '@/components/item-categories/ItemCategorySelect';
import { TagInput } from '@/components/TagInput';

interface PriceRowEditorProps {
    projectFeeSets: ProjectFeeSet[];
    value: PriceRow;
    onChange?(row: PriceRow): void;
    onDeleteClick(): void;
    showLabels?: boolean;
    rootItemCategoryFilter?(itemCategory: ItemCategory): boolean;
}

const PriceRowEditor: React.FC<PriceRowEditorProps> = ({
    value,
    onChange,
    onDeleteClick,
    projectFeeSets,
    showLabels,
    rootItemCategoryFilter,
}) => {
    const triggerChange = (changes: Partial<PriceRow>) => {
        triggerHandler(onChange, { ...value, ...changes });
    };
    const isMobile = useIsMobile();

    const selectedFeeSet = projectFeeSets.find((x) => x.id === value.projectFeeSetId);

    const renderTitle = (title: TranslatableText) => {
        if (!isMobile && !showLabels) {
            return null;
        }

        return <span className={theme.title}>{title}</span>;
    };

    return (
        <div className={theme.container}>
            {!isMobile && <Button color="danger" className={theme.deleteButton} icon={faTrashAlt} onClick={onDeleteClick} />}
            <div className={theme.row}>
                <div className={theme.topRow}>
                    <label>
                        {renderTitle(<Trans>Description</Trans>)}

                        <ValidatingInput
                            placeholder={t`Description`}
                            type="text"
                            className="form-control"
                            onChange={(e) => triggerChange({ description: e.target.value })}
                            value={value.description}
                            required
                        />
                    </label>
                    <label>
                        {renderTitle(<Trans>Category</Trans>)}

                        <ItemCategorySelect
                            className={classnames('form-control-unstyled', theme.categoryDropDownContainer)}
                            value={value.itemCategoryId}
                            onChange={(nv) => triggerChange({ itemCategoryId: nv.value })}
                            rootItemCategoryFilter={rootItemCategoryFilter}
                            required
                            fieldName={t`Category`}
                        />
                    </label>
                    <label>
                        {renderTitle(<Trans>Price</Trans>)}

                        <ValidatingInput
                            placeholder={t`Total price`}
                            type="number"
                            className="form-control"
                            onChange={(e) => triggerChange({ price: toNullableNumber(e.target.value) })}
                            value={value.price === null ? '' : value.price}
                            required
                        />
                    </label>
                    <label>
                        {renderTitle(<Trans>Fees</Trans>)}

                        <ValidatingSelect
                            className="form-control"
                            value={value.projectFeeSetId}
                            onChange={(e) => triggerChange({ projectFeeSetId: +e.target.value })}>
                            {projectFeeSets.map((x) => (
                                <option key={x.id} value={x.id}>
                                    {x.name}
                                </option>
                            ))}
                        </ValidatingSelect>
                    </label>

                    <label>
                        {renderTitle(<Trans>Total</Trans>)}
                        <FeePreviewCompact
                            quantity={1}
                            price={value.price || 0}
                            projectFeeSet={selectedFeeSet}
                            onUpdate={(calculation) => triggerChange({ priceCalculation: calculation })}
                        />
                    </label>
                    {isMobile && (
                        <Button color="danger" className={theme.deleteButton} icon={faTrashAlt} onClick={onDeleteClick}>
                            Delete row
                        </Button>
                    )}
                </div>

                <TagInput
                    className={theme.tagInput}
                    value={value.tags}
                    onTagsChange={(tags) => triggerChange({ tags: tags })}
                    placeholder={t`Tags`}
                />
            </div>
        </div>
    );
};

export default PriceRowEditor;
